/**
 * Lead Management Service
 *
 * Static methods for querying and managing marketing leads from
 * the marketing_leads/{type}/entries/ collection structure.
 * Follows PlatformMetricsService.ts pattern.
 *
 * TODO: Auto-detect conversion — when a company is onboarded via /companies/onboard,
 *       match the new user's email against marketing_leads to auto-set converted=true.
 *       Likely a Cloud Function trigger on users collection create.
 */

import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  onSnapshot,
  orderBy,
  limit,
  Timestamp,
  Unsubscribe,
  arrayUnion,
} from 'firebase/firestore';
import type {
  MarketingLead,
  LeadType,
  LeadStatus,
  DashboardStats,
  StatusChange,
} from '../types/leads';
import { toDate } from '../types/leads';
import { csvCell } from '../utils/leadOutputSafety';

const LEAD_TYPES: LeadType[] = ['scorecard', 'founders', 'contacts', 'waitlist'];

function getLeadCollection(type: LeadType) {
  return collection(db, 'marketing_leads', type, 'entries');
}

export class LeadManagementService {

  /**
   * Fetch leads of a specific type with optional filters.
   */
  static async getLeads(
    type: LeadType,
    filters?: {
      status?: LeadStatus;
      source?: string;
      limit?: number;
    }
  ): Promise<MarketingLead[]> {
    try {
      const constraints: any[] = [orderBy('timestamp', 'desc')];

      if (filters?.status) {
        constraints.unshift(where('status', '==', filters.status));
      }
      if (filters?.limit) {
        constraints.push(limit(filters.limit));
      }

      const q = query(getLeadCollection(type), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(d => ({
        id: d.id,
        type,
        ...d.data(),
      } as MarketingLead));
    } catch (error) {
      console.error(`Error fetching ${type} leads:`, error);
      return [];
    }
  }

  /**
   * Subscribe to real-time lead updates.
   */
  static subscribeToLeads(
    type: LeadType,
    callback: (leads: MarketingLead[]) => void
  ): Unsubscribe {
    const q = query(getLeadCollection(type), orderBy('timestamp', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const leads = snapshot.docs.map(d => ({
        id: d.id,
        type,
        ...d.data(),
      } as MarketingLead));
      callback(leads);
    }, (error) => {
      console.error(`Error subscribing to ${type} leads:`, error);
    });
  }

  /**
   * Get a single lead by type and ID.
   */
  static async getLead(type: LeadType, docId: string): Promise<MarketingLead | null> {
    try {
      const docRef = doc(db, 'marketing_leads', type, 'entries', docId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return { id: snap.id, type, ...snap.data() } as MarketingLead;
    } catch (error) {
      console.error('Error fetching lead:', `${type}/${docId}`, error);
      return null;
    }
  }

  /**
   * Update lead status with audit trail.
   */
  static async updateLeadStatus(
    type: LeadType,
    docId: string,
    newStatus: LeadStatus,
    changedBy: string,
    note?: string
  ): Promise<void> {
    const docRef = doc(db, 'marketing_leads', type, 'entries', docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Lead not found');

    const currentStatus = snap.data().status || 'new';
    const statusChange: StatusChange = {
      from: currentStatus,
      to: newStatus,
      changedBy,
      changedAt: new Date(),
      ...(note ? { note } : {}),
    };

    await updateDoc(docRef, {
      status: newStatus,
      statusHistory: arrayUnion(statusChange),
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Toggle bookedDemo flag and sync status if needed.
   */
  static async toggleBookedDemo(
    type: LeadType,
    docId: string,
    value: boolean,
    changedBy: string
  ): Promise<void> {
    const docRef = doc(db, 'marketing_leads', type, 'entries', docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Lead not found');

    const currentStatus = snap.data().status || 'new';
    const updates: Record<string, any> = {
      bookedDemo: value,
      updatedAt: Timestamp.now(),
    };

    // Auto-advance status if setting demo booked and status is before demo_booked
    if (value && ['new', 'contacted'].includes(currentStatus)) {
      const statusChange: StatusChange = {
        from: currentStatus,
        to: 'demo_booked',
        changedBy,
        changedAt: new Date(),
        note: 'Auto-set when demo was booked',
      };
      updates.status = 'demo_booked';
      updates.statusHistory = arrayUnion(statusChange);
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Toggle converted flag and sync status.
   */
  static async toggleConverted(
    type: LeadType,
    docId: string,
    value: boolean,
    changedBy: string
  ): Promise<void> {
    const docRef = doc(db, 'marketing_leads', type, 'entries', docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Lead not found');

    const currentStatus = snap.data().status || 'new';
    const updates: Record<string, any> = {
      converted: value,
      updatedAt: Timestamp.now(),
    };

    if (value && currentStatus !== 'converted') {
      const statusChange: StatusChange = {
        from: currentStatus,
        to: 'converted',
        changedBy,
        changedAt: new Date(),
        note: 'Marked as converted',
      };
      updates.status = 'converted';
      updates.statusHistory = arrayUnion(statusChange);
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Add an admin note to a lead.
   */
  static async addNote(
    type: LeadType,
    docId: string,
    note: string,
    author: string
  ): Promise<void> {
    const docRef = doc(db, 'marketing_leads', type, 'entries', docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Lead not found');

    const currentNotes = snap.data().notes || '';
    const timestamp = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' });
    const newNote = `[${timestamp} - ${author}] ${note}`;
    const updatedNotes = currentNotes ? `${currentNotes}\n${newNote}` : newNote;

    await updateDoc(docRef, {
      notes: updatedNotes,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Aggregate dashboard statistics across all lead types.
   */
  static async getDashboardStats(): Promise<DashboardStats> {
    const stats: DashboardStats = {
      totalLeads: 0,
      leadsThisMonth: 0,
      demosBooked: 0,
      conversionRate: 0,
      byStatus: {
        new: 0, contacted: 0, demo_booked: 0, demo_completed: 0,
        negotiating: 0, converted: 0, lost: 0,
      },
      bySource: {},
      byType: { scorecard: 0, founders: 0, contacts: 0, waitlist: 0 },
      recentLeads: [],
    };

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const allLeads: MarketingLead[] = [];

    for (const type of LEAD_TYPES) {
      try {
        const q = query(getLeadCollection(type), orderBy('timestamp', 'desc'));
        const snapshot = await getDocs(q);

        snapshot.docs.forEach(d => {
          const lead = { id: d.id, type, ...d.data() } as MarketingLead;
          allLeads.push(lead);
          stats.totalLeads++;
          stats.byType[type]++;

          // Status counts
          const status = lead.status || 'new';
          if (stats.byStatus[status] !== undefined) {
            stats.byStatus[status]++;
          }

          // Demo count
          if (lead.bookedDemo) stats.demosBooked++;

          // Monthly count
          const ts = toDate(lead.timestamp);
          if (ts >= startOfMonth) stats.leadsThisMonth++;

          // Source counts
          const source = lead.source?.utm_source || 'direct';
          stats.bySource[source] = (stats.bySource[source] || 0) + 1;
        });
      } catch (error) {
        console.error(`Error aggregating ${type} stats:`, error);
      }
    }

    // Conversion rate
    if (stats.totalLeads > 0) {
      stats.conversionRate = Math.round((stats.byStatus.converted / stats.totalLeads) * 100);
    }

    // Recent leads (last 10)
    stats.recentLeads = allLeads
      .sort((a, b) => toDate(b.timestamp).getTime() - toDate(a.timestamp).getTime())
      .slice(0, 10);

    return stats;
  }

  /**
   * Export leads to CSV string.
   */
  static async exportToCSV(type: LeadType): Promise<string> {
    const leads = await this.getLeads(type);
    if (leads.length === 0) return '';

    // Build header from first lead's keys
    const commonHeaders = [
      'id', 'email', 'status', 'bookedDemo', 'converted', 'timestamp',
      'utm_source', 'utm_medium', 'utm_campaign', 'referralCode',
    ];

    const typeHeaders: Record<LeadType, string[]> = {
      scorecard: ['firstName', 'lastName', 'phone', 'company', 'score', 'tier'],
      founders: ['fullName', 'mobile', 'companyName', 'numberOfOffices', 'numberOfBrokers', 'brokerType', 'annualLoanVolume', 'currentTrackingMethod', 'biggestChallenge'],
      contacts: ['name', 'company', 'message'],
      waitlist: ['name', 'email'],
    };

    const headers = [...typeHeaders[type], ...commonHeaders];

    const rows = leads.map(lead => {
      return headers.map(h => {
        if (h === 'timestamp') return toDate(lead.timestamp).toISOString();
        if (h.startsWith('utm_') || h === 'referralCode') {
          return csvCell(lead.source?.[h as keyof typeof lead.source]);
        }
        const val = (lead as any)[h];
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        return csvCell(val);
      }).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  }
}
